import type { IPhase, IProgressMetrics, IRoadmap, ITask, TTaskStatus } from '../data/types';

const statusWeight: Record<TTaskStatus, number> = {
  done: 1,
  in_progress: 0.5,
  todo: 0,
};

export const getPhaseMetrics = (phase: IPhase): IProgressMetrics => {
  const total = phase.tasks.length;
  const done = phase.tasks.filter((t) => t.status === 'done').length;
  const inProgress = phase.tasks.filter((t) => t.status === 'in_progress').length;
  const todo = phase.tasks.filter((t) => t.status === 'todo').length;
  const percent =
    total === 0
      ? 0
      : Math.round(
          (phase.tasks.reduce((sum, t) => sum + statusWeight[t.status], 0) / total) * 100,
        );
  return { total, done, inProgress, todo, percent };
};

export const getOverallProgress = (roadmap: IRoadmap): IProgressMetrics => {
  const allTasks = roadmap.phases.flatMap((p) => p.tasks);
  const total = allTasks.length;
  const done = allTasks.filter((t) => t.status === 'done').length;
  const inProgress = allTasks.filter((t) => t.status === 'in_progress').length;
  const todo = allTasks.filter((t) => t.status === 'todo').length;

  const totalWeight = roadmap.phases.reduce((s, p) => s + p.weight, 0);
  const weightedSum = roadmap.phases.reduce((sum, phase) => {
    const metrics = getPhaseMetrics(phase);
    return sum + (metrics.percent / 100) * phase.weight;
  }, 0);
  const percent = totalWeight === 0 ? 0 : Math.round((weightedSum / totalWeight) * 100);

  return { total, done, inProgress, todo, percent };
};

export const countByCategory = (tasks: ITask[]) => {
  const map: Record<string, { total: number; done: number }> = {};
  tasks.forEach((t) => {
    const k = t.category;
    if (!map[k]) map[k] = { total: 0, done: 0 };
    map[k].total += 1;
    if (t.status === 'done') map[k].done += 1;
  });
  return map;
};

export const countAggregates = (roadmap: IRoadmap) => {
  const all = roadmap.phases.flatMap((p) => p.tasks);
  const pages = new Set(all.flatMap((t) => t.pages ?? []));
  const endpoints = new Set(all.flatMap((t) => t.endpoints ?? []));
  const doneTasks = all.filter((t) => t.status === 'done').length;
  return {
    tasks: all.length,
    doneTasks,
    pages: pages.size,
    endpoints: endpoints.size,
  };
};
